import { useOrganization, useUser } from "@clerk/expo";
import { useRouter } from "expo-router";
import {
  Archive,
  Calendar,
  Copy,
  FileText,
  FolderInput,
  RotateCcw,
  Share2,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import { MoveToModal } from "@/components/folders";
import {
  ActionMenu,
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  Badge,
  BadgeText,
  Box,
  Card,
  DeleteDialog,
  HStack,
  Image,
  Media,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import {
  useDatetimeUtils,
  useLocalization,
  useOrganizationMembers,
} from "@/hooks";
import API from "@/hooks/api";
import { Study } from "@/types/resources";
import CreateStudy from "./CreateStudy";
import ShareModal from "./ShareModal";
import ShareResultsModal from "./ShareResultsModal";

/** Stable identifiers for the menu entries `StudyCard` can render. Callers
    drop entries via `forbiddenActions` rather than passing booleans per
    entry — keeps the surface from growing one prop per action over time. */
export type StudyCardAction =
  | "duplicate"
  | "share"
  | "move"
  | "shareResults"
  | "archive"
  | "restore"
  | "deletePermanently";

interface StudyCardProps {
  /** Menu entries to forbid for this card. Empty / undefined keeps the
      full action set (the default for the studies grid). Quick-insights
      uses this to forbid `"move"` since folders aren't part of the
      use-case workflow. */
  forbiddenActions?: readonly StudyCardAction[];
  loading?: boolean;
  onDeleted?: () => void;
  onDuplicated?: (study: Study) => void;
  onMoved?: () => void;
  /** Override the default tap-to-open navigation. When provided, replaces
      the built-in `router.push("/(dashboard)/studies/[id]")` so the card
      can be reused in flows like quick-insights that route to a different
      detail screen. */
  onPress?: () => void;
  study?: Study;
}

const StudyCard: FC<StudyCardProps> = ({
  forbiddenActions,
  loading,
  onDeleted,
  onDuplicated,
  onMoved,
  onPress,
  study,
}) => {
  const { user } = useUser();
  const { membership, organization } = useOrganization();
  const { formatMemberName, users: membersMap } = useOrganizationMembers();
  const author = study?.created_by_id
    ? membersMap[study.created_by_id]
    : undefined;
  const { i18n, browserLocale } = useLocalization();
  const { formatDate } = useDatetimeUtils();
  const router = useRouter();
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    archiveStudy,
    error: archiveError,
    loading: archiving,
  } = API.Studies.useArchiveStudy();

  const {
    deleteStudy,
    error: deleteError,
    loading: deleting,
  } = API.Studies.useDeleteStudy();

  const { updateStudy } = API.Studies.useUpdateStudy();

  const {
    error: shareError,
    link: shareLink,
    loading: sharing,
    shareStudy,
  } = API.Studies.useShareStudy(study?.id ?? "");

  const isArchived = !!study?.archived_at;
  const hasCompletedRun = study?.last_run?.conclusion === "success";
  const isAdmin = user?.publicMetadata?.admin === true;
  const canDelete =
    isAdmin || !organization || membership?.role === "org:admin";

  const menuActions = useMemo(() => {
    /* Build the full ordered list with stable `id`s, then drop any entry
       whose id is in `forbiddenActions`. Centralising the filter here
       keeps ordering / divider logic in one place — callsites only declare
       intent, not menu structure. */
    const forbidden = new Set<StudyCardAction>(forbiddenActions ?? []);

    const all = isArchived
      ? [
          ...(study?.can_edit
            ? [
                {
                  color: "typography" as const,
                  icon: RotateCcw,
                  id: "restore" as const,
                  label: i18n.t("misc.restore"),
                  onPress: () => setShowRestoreDialog(true),
                },
              ]
            : []),
        ]
      : [
          {
            color: "typography" as const,
            icon: Copy,
            id: "duplicate" as const,
            label: i18n.t("misc.duplicate"),
            onPress: () => setShowDuplicateDialog(true),
          },
          ...(study?.can_edit && organization
            ? [
                {
                  color: "typography" as const,
                  divider: true as const,
                  icon: UserPlus,
                  id: "share" as const,
                  label: i18n.t("sharing.share"),
                  onPress: () => setShowShareModal(true),
                },
              ]
            : []),
          ...(study?.can_edit
            ? [
                {
                  color: "typography" as const,
                  icon: FolderInput,
                  id: "move" as const,
                  label: i18n.t("misc.move"),
                  onPress: () => setShowMoveModal(true),
                },
              ]
            : []),
          {
            color: "typography" as const,
            disabled: !hasCompletedRun || sharing,
            icon: Share2,
            id: "shareResults" as const,
            label: i18n.t("studies.shareResults"),
            onPress: async () => {
              setShowShareDialog(true);
              if (shareLink) return;
              if (
                study?.share_link &&
                (!study?.share_expires_at ||
                  new Date(study?.share_expires_at) > new Date())
              )
                return;
              shareStudy(30);
            },
          },
          ...(study?.can_edit
            ? [
                {
                  color: "error" as const,
                  divider: true as const,
                  icon: Archive,
                  id: "archive" as const,
                  label: i18n.t("misc.archive"),
                  onPress: () => setShowArchiveDialog(true),
                },
              ]
            : []),
          ...(canDelete
            ? [
                {
                  color: "error" as const,
                  divider: !study?.can_edit ? (true as const) : undefined,
                  icon: Trash2,
                  id: "deletePermanently" as const,
                  label: i18n.t("misc.deletePermanently"),
                  onPress: () => setShowDeleteDialog(true),
                },
              ]
            : []),
        ];

    const filtered = all.filter((action) => !forbidden.has(action.id));
    return filtered.length > 0 ? filtered : null;
  }, [
    canDelete,
    forbiddenActions,
    hasCompletedRun,
    i18n,
    isArchived,
    organization,
    shareLink,
    sharing,
    shareStudy,
    study,
  ]);

  if (loading) {
    return (
      <Box className="h-full">
        <Card className="border border-outline-200 rounded-2xl p-0 overflow-hidden h-full">
          <VStack className="h-full">
            <Skeleton className="w-full h-32 rounded-none" />
            <VStack className="p-5 flex-1 justify-between">
              <VStack>
                <HStack className="items-center gap-2 mb-2">
                  <Skeleton className="h-4 w-16 rounded-full" />
                  <Skeleton className="h-4 w-24 rounded" />
                </HStack>
                <Skeleton className="h-4 w-3/4 rounded mb-1" />
                <Skeleton className="h-3 w-full rounded mb-3" />
              </VStack>
              <HStack className="items-center justify-between w-full">
                <Skeleton className="h-3 w-24 rounded" />
                <HStack className="items-center gap-1.5">
                  <Skeleton className="w-4 h-4 rounded-full" />
                  <Skeleton className="h-3 w-16 rounded" />
                </HStack>
              </HStack>
            </VStack>
          </VStack>
        </Card>
      </Box>
    );
  }

  if (!study) {
    return null;
  }

  const handleNavigate = () => {
    if (onPress) {
      onPress();
      return;
    }
    router.push({
      pathname: "/(dashboard)/studies/[id]",
      params: { id: study.id },
    });
  };

  const handleArchiveConfirm = async () => {
    const result = await archiveStudy(study.id, true);
    if (result) {
      setShowArchiveDialog(false);
      onDeleted?.();
    }
  };

  let tagAction: "warning" | "success" | "error" | "info" | "muted";
  let tagText: string;

  if (study.last_run && study.last_run.conclusion === null) {
    tagAction = "muted";
    tagText = i18n.t("misc.in_progress");
  } else if (study.last_run?.conclusion === "success") {
    tagAction = "success";
    tagText = i18n.t("misc.completed");
  } else if (study.last_run?.conclusion === "failed") {
    tagAction = "error";
    tagText = i18n.t("misc.failed");
  } else if (study.num_groups > 0 && study.num_questions > 0) {
    tagAction = "info";
    tagText = i18n.t("misc.ready");
  } else {
    tagAction = "warning";
    tagText = i18n.t("misc.draft");
  }

  const description = study.description || i18n.t("studies.noDescription");
  const descriptionClassName = study.description
    ? "text-xs text-typography-500 line-clamp-2 mb-3"
    : "text-xs text-typography-500 italic line-clamp-2 mb-3";

  const date = formatDate(study.created_at);

  return (
    <>
      <Box className="h-full">
        <Pressable className="h-full" onPress={handleNavigate}>
          <Card className="relative border border-outline-200 rounded-2xl p-0 overflow-hidden h-full hover:border-outline-300 hover:shadow-soft-2 hover:-translate-y-0.5 transition-all duration-300">
            {menuActions && (
              <Box className="absolute top-3 right-3 z-10">
                <ActionMenu actions={menuActions} />
              </Box>
            )}

            <VStack className="h-full">
              {/* Image / placeholder */}
              {study.picture_url ? (
                <Box className="w-full h-32 overflow-hidden">
                  <Media
                    alt={study.name}
                    className="!rounded-none !border-0"
                    height={128}
                    resizeMode="cover"
                    source={study.picture_url}
                    width="100%"
                  />
                </Box>
              ) : (
                <Box className="w-full h-32 bg-primary-50 items-center justify-center">
                  <FileText color="rgb(var(--color-primary-600))" size={32} />
                </Box>
              )}

              <VStack className="p-5 flex-1 justify-between">
                <VStack>
                  <HStack className="items-center gap-2 mb-2">
                    <Badge
                      action={tagAction}
                      className="px-2 py-0.5 rounded-full"
                      variant="solid"
                    >
                      <BadgeText className="text-2xs font-medium lowercase">
                        {tagText}
                      </BadgeText>
                    </Badge>
                    <Text className="text-xs text-typography-500">
                      {i18n.t("dashboard.questionCount", {
                        count: study.num_questions,
                      })}
                    </Text>
                  </HStack>
                  <Text className="text-sm font-semibold text-typography-900 mb-1">
                    {study.name}
                  </Text>
                  <Text className={descriptionClassName}>{description}</Text>
                </VStack>

                <HStack className="items-center justify-between w-full">
                  <HStack className="items-center gap-3">
                    {!!study.num_respondents && (
                      <HStack className="items-center gap-1">
                        <Users
                          className="text-typography-500"
                          size={14}
                          strokeWidth={2}
                        />
                        <Text className="text-xs text-typography-500">
                          {i18n.numberToDelimited(study.num_respondents, {
                            delimiter: browserLocale.digitGroupingSeparator!,
                          })}
                        </Text>
                      </HStack>
                    )}
                    {date && (
                      <HStack className="items-center gap-1">
                        <Calendar
                          className="text-typography-500"
                          size={14}
                          strokeWidth={2}
                        />
                        <Text className="text-xs text-typography-500">
                          {date}
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                  {author && (
                    <HStack className="items-center gap-1.5">
                      <Image
                        alt={formatMemberName(author, true)}
                        className="w-4 h-4 rounded-full"
                        source={{ uri: author.imageUrl }}
                      />
                      <Text className="text-xs font-medium text-typography-500">
                        {formatMemberName(author, true)}
                      </Text>
                    </HStack>
                  )}
                </HStack>
              </VStack>
            </VStack>
          </Card>
        </Pressable>
      </Box>

      <DeleteDialog
        body={i18n.t("studies.confirmArchiveMessage", { name: study.name })}
        deleteButtonText={i18n.t("misc.archive")}
        error={archiveError}
        isOpen={showArchiveDialog}
        loading={archiving}
        loadingText={i18n.t("misc.archiving")}
        onCancel={() => setShowArchiveDialog(false)}
        onDelete={handleArchiveConfirm}
        title={i18n.t("studies.archiveStudy")}
      />

      <DeleteDialog
        body={i18n.t("studies.confirmRestoreMessage")}
        deleteButtonText={i18n.t("misc.restore")}
        error={archiveError}
        isOpen={showRestoreDialog}
        loading={archiving}
        loadingText={i18n.t("misc.restoring")}
        onCancel={() => setShowRestoreDialog(false)}
        onDelete={async () => {
          const result = await archiveStudy(study.id, false);
          if (result) {
            setShowRestoreDialog(false);
            onDeleted?.();
          }
        }}
        title={i18n.t("studies.restoreStudy")}
      />

      <DeleteDialog
        body={i18n.t("studies.confirmDeletePermanentlyMessage")}
        deleteButtonText={i18n.t("misc.deletePermanently")}
        error={deleteError}
        isOpen={showDeleteDialog}
        loading={deleting}
        loadingText={i18n.t("misc.deleting")}
        onCancel={() => setShowDeleteDialog(false)}
        onDelete={async () => {
          const result = await deleteStudy(study.id);
          if (result) {
            setShowDeleteDialog(false);
            onDeleted?.();
          }
        }}
        title={i18n.t("studies.deleteStudyPermanently")}
      />

      <AlertDialog
        isOpen={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
      >
        <AlertDialogBackdrop />
        <AlertDialogContent className="max-w-4xl">
          <AlertDialogBody className="p-2">
            <CreateStudy
              className="border-0 p-0"
              folderId={study.folder_id}
              initialDescription={study.description || undefined}
              initialName={i18n.t("studies.copyOf", { name: study.name })}
              onCancel={() => setShowDuplicateDialog(false)}
              onCreated={(newStudy) => {
                setShowDuplicateDialog(false);
                onDuplicated?.(newStudy);
                router.push(`/(dashboard)/studies/${newStudy.id}`);
              }}
              studyId={study.id}
            />
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialog>

      <ShareResultsModal
        error={shareError}
        isOpen={showShareDialog}
        link={shareLink ?? study.share_link}
        loading={sharing}
        onClose={() => setShowShareDialog(false)}
        onRetry={() => shareStudy(30)}
      />

      {showShareModal && (
        <ShareModal
          isOpen
          onClose={() => setShowShareModal(false)}
          ownerId={study.created_by_id}
          resourceId={study.id}
          resourceName={study.name}
          resourceType="study"
        />
      )}

      {showMoveModal && (
        <MoveToModal
          currentFolderId={study.folder_id}
          isOpen={true}
          itemName={study.name}
          onClose={() => setShowMoveModal(false)}
          onMove={async (targetFolderId) => {
            const result = await updateStudy(study.id, {
              description: study.description,
              folder_id: targetFolderId,
              name: study.name,
              picture: study.picture,
            });
            if (result) onMoved?.();
            return !!result;
          }}
          type="studies"
        />
      )}
    </>
  );
};

export default StudyCard;
